import { useMemo, useState } from 'react'
import moment from 'moment'
import apiPost from 'lib/network/apiPost'
import notify from 'components/atoms/Notify'

const toLocalDateTimeInput = dateValue => {
  if (!dateValue) return ''
  return moment(dateValue).format('YYYY-MM-DDTHH:mm')
}

const ExtendBookingModal = ({ bookingData, bookingId, onSuccess, close }) => {
  const startDate = bookingData?.rentalEndDate
  const extensionStartDate = useMemo(() => moment(startDate).add(1, 'minute'), [startDate])
  const [newEndDate, setNewEndDate] = useState(toLocalDateTimeInput(moment(extensionStartDate).add(1, 'day')))
  const [submitting, setSubmitting] = useState(false)

  const minEndDate = useMemo(() => toLocalDateTimeInput(moment(extensionStartDate).add(1, 'minute')), [extensionStartDate])

  const handleSubmit = async e => {
    e.preventDefault()

    if (!newEndDate) {
      notify.error('Please select a new end date')
      return
    }

    if (moment(newEndDate).isSameOrBefore(extensionStartDate)) {
      notify.error('New booking end date must be after new booking start date')
      return
    }

    try {
      setSubmitting(true)
      const response = await apiPost(`booking/${bookingId}/extend`, {
        newRentalEndDate: moment(newEndDate).toISOString(),
      })

      if (response?.success) {
        notify.success('Booking extended successfully')
        // eslint-disable-next-line
        if (typeof onSuccess === 'function') {
          onSuccess(response?.data)
        }
        close?.()
      }
    } catch (error) {
      notify.error(error?.message || 'Failed to extend booking')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex h-full flex-col">
      <div className="space-y-4 px-1 py-2">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700" htmlFor="newStartDate">
              New booking start date
            </label>
            <input
              id="newStartDate"
              type="datetime-local"
              className="block w-full rounded-md border border-gray-300 bg-gray-100 px-3 py-2 text-sm text-gray-500 shadow-sm cursor-not-allowed"
              value={toLocalDateTimeInput(extensionStartDate)}
              disabled
              readOnly
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700" htmlFor="newEndDate">
              New booking end date
            </label>
            <input
              id="newEndDate"
              type="datetime-local"
              className="block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-dark-500 focus:outline-none focus:ring-1 focus:ring-dark-500"
              value={newEndDate}
              min={minEndDate}
              onChange={e => setNewEndDate(e.target.value)}
              required
            />
          </div>
        </div>
      </div>

      <div className="mt-5 flex justify-end border-t border-gray-100 pt-4">
        <button
          type="button"
          className="mr-3 inline-flex items-center rounded-md border shadow px-4 py-2 text-sm font-medium text-dark-500 bg-white transition duration-150 ease-in-out hover:bg-gray-50 disabled:opacity-50"
          onClick={() => close?.()}
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="inline-flex items-center rounded-md border border-transparent px-4 py-2 text-sm font-medium text-white bg-dark-600 hover:bg-dark-500 focus:outline-none transition duration-150 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={submitting}
        >
          {submitting ? 'Extending...' : 'Extend Booking'}
        </button>
      </div>
    </form>
  )
}

export default ExtendBookingModal
